import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Lock, Shield, AlertTriangle } from 'lucide-react';
import Disclaimer from '../../components/Disclaimer';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const SecuriteDonneesParisEnLigne = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Disclaimer />
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/news" data-discover="true" className="inline-flex items-center text-purple-600 hover:text-purple-700 mb-8">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour aux actualités
        </Link>

        <article className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <div className="flex items-center gap-2 text-sm text-purple-600 mb-4">
            <span>15.11.2025</span>
            <span className="text-gray-400">•</span>
            <span className="font-semibold">Sécurité</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Sécurité des Données : Ce Que Vous Devez Savoir
          </h1>
          
          <div className="prose prose-lg max-w-none text-gray-700 space-y-6 leading-relaxed">
            <p className="text-xl text-gray-600 font-medium">
              La protection de vos données personnelles et bancaires est fondamentale quand vous pariez en ligne. Les sites de paris collectent 
              des informations sensibles : identité, coordonnées bancaires, historique de transactions. Comprendre comment ces données sont 
              protégées et quelles sont les bonnes pratiques à suivre est essentiel pour parier en toute sécurité. Voici tout ce que vous 
              devez savoir.
            </p>

            <div className="bg-purple-50 border-l-4 border-purple-600 p-6 my-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Lock className="w-6 h-6 text-purple-600" />
                Comment les Sites Protègent Vos Données
              </h2>
              <p>
                Les sites agréés en France sont soumis au RGPD (Règlement Général sur la Protection des Données) et doivent utiliser des 
                technologies de cryptage avancées pour protéger vos informations. Le cryptage SSL/TLS sécurise toutes les communications entre 
                votre navigateur et le site, empêchant les interceptions. Vos données bancaires sont cryptées avant stockage et ne sont jamais 
                transmises en clair.
              </p>
              <p>
                Les sites sérieux utilisent aussi des systèmes de sécurité multicouches : authentification à deux facteurs, surveillance des 
                activités suspectes, et sauvegardes régulières. Ces mesures protègent non seulement contre les attaques externes mais aussi 
                contre les accès non autorisés internes. Vérifiez toujours que le site utilise bien le cryptage (recherchez le cadenas dans 
                la barre d'adresse de votre navigateur).
              </p>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-10 mb-4">
              Les Données Collectées et Leur Utilisation
            </h2>
            <p>
              Les sites de paris collectent plusieurs types de données : identité (nom, date de naissance, adresse), coordonnées (email, 
              téléphone), données bancaires (pour les transactions), et données de comportement (historique de paris, préférences). Chaque 
              type de donnée a une finalité spécifique et doit être utilisé uniquement dans ce cadre.
            </p>
            <p>
              Les sites ne peuvent pas utiliser vos données à d'autres fins sans votre consentement explicite. Ils ne peuvent pas les vendre 
              à des tiers sans votre autorisation. Ils doivent aussi vous permettre d'accéder à vos données, de les corriger, et de demander 
              leur suppression. Ces droits sont garantis par le RGPD et doivent être respectés.
            </p>

            <div className="bg-blue-50 border-l-4 border-blue-600 p-6 my-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Shield className="w-6 h-6 text-blue-600" />
                Les Bonnes Pratiques à Suivre
              </h2>
              <p>
                Pour maximiser votre sécurité, suivez ces pratiques essentielles : utilisez un mot de passe fort et unique pour chaque site 
                (combinaison de lettres, chiffres, caractères spéciaux), activez l'authentification à deux facteurs si disponible, ne partagez 
                jamais vos identifiants, et vérifiez régulièrement l'historique de vos transactions pour détecter toute activité suspecte.
              </p>
              <p>
                Évitez aussi de vous connecter depuis des réseaux Wi-Fi publics non sécurisés, utilisez des méthodes de paiement sécurisées 
                (évitez d'enregistrer vos coordonnées bancaires si possible), et lisez attentivement les politiques de confidentialité avant 
                de vous inscrire. Ces pratiques simples réduisent considérablement les risques.
              </p>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-10 mb-4">
              Les Signaux d'Alarme à Surveiller
            </h2>
            <p>
              Certains signaux doivent vous alerter immédiatement : des emails suspects demandant vos identifiants (les sites légitimes ne 
              demandent jamais cela par email), des transactions que vous n'avez pas effectuées, des changements dans votre compte que vous 
              n'avez pas initiés, ou des demandes d'informations excessives lors de l'inscription.
            </p>
            <p>
              Si vous remarquez ces signaux, agissez immédiatement : changez votre mot de passe, contactez le service client du site, et si 
              nécessaire, contactez votre banque pour bloquer les transactions suspectes. Les sites sérieux ont des procédures claires pour 
              gérer ces situations et protéger leurs clients.
            </p>

            <div className="bg-green-50 border-l-4 border-green-600 p-6 my-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-6 h-6 text-green-600" />
                Le Partage de Données avec des Tiers
              </h2>
              <p>
                Les sites peuvent partager certaines données avec des partenaires (processeurs de paiement, fournisseurs de services techniques), 
                mais uniquement dans le cadre strict de la fourniture du service. Ils ne peuvent pas vendre vos données à des fins marketing 
                sans votre consentement explicite. Vérifiez toujours les politiques de confidentialité pour comprendre quelles données sont 
                partagées et avec qui.
              </p>
              <p>
                Vous avez le droit de refuser ces partages sans impact sur votre accès aux services essentiels. Si un site vous force à accepter 
                des partages excessifs pour utiliser le service, c'est un signal d'alarme. Les sites sérieux respectent votre choix et offrent 
              des alternatives.
            </p>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-10 mb-4">
              Vos Droits et Comment les Exercer
            </h2>
            <p>
              Le RGPD vous donne plusieurs droits essentiels : droit d'accès (consulter vos données), droit de rectification (corriger les 
              erreurs), droit à l'effacement (demander la suppression), droit à la portabilité (récupérer vos données), et droit d'opposition 
              (refuser certains traitements). Ces droits sont gratuits et doivent être exercés dans un délai raisonnable.
            </p>
            <p>
              Pour exercer ces droits, contactez le service client du site ou utilisez les formulaires prévus à cet effet. Les sites doivent 
              répondre dans un délai d'un mois. Si vous n'obtenez pas de réponse ou si vous n'êtes pas satisfait, vous pouvez porter plainte 
              auprès de la CNIL (Commission Nationale de l'Informatique et des Libertés).
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-10 mb-4">
              Conclusion : La Sécurité Commence par Vous
            </h2>
            <p>
              La sécurité de vos données dépend à la fois des mesures techniques des sites et de vos propres pratiques. Choisissez uniquement 
              des sites agréés qui respectent le RGPD, utilisez des mots de passe forts et uniques, activez l'authentification à deux facteurs, 
              surveillez régulièrement vos comptes, et exercez vos droits si nécessaire.
            </p>
            <p>
              La protection des données n'est pas seulement une obligation légale : c'est une responsabilité partagée entre les sites et les 
              parieurs. En suivant ces bonnes pratiques et en restant vigilant, vous pouvez parier en ligne en toute sécurité, sachant que 
              vos données personnelles et bancaires sont protégées. La sécurité ne doit jamais être un compromis : choisissez toujours des 
              sites qui la prennent au sérieux.
            </p>
          </div>
        </article>
      </div>

      <Footer />
    </div>
  );
};

export default SecuriteDonneesParisEnLigne;

